import React, { useMemo } from 'react';
import { DynamicResultTable } from '../../dynamic-result-table';
import { FormattedDate } from '../../formatted';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { EntityWithNotes } from '@atlassian/cssxp-types-audit';
import { useLazyQuery, useQuery } from '@apollo/client';
import { fetchAuditNotesForEntity } from './queries/fetch-audit-notes';
import { fetchAuditNoteAuthors } from './queries/fetch-audit-note-authors';
import { useFeatureToggle } from '../../hooks/src/use-feature-toggle';
import { ENABLE_SUPPORT_TICKET_AND_NOTE } from '@atlassian/cssxp-utils-generic';
import classNames from 'classnames';
import validator from 'validator';
import { DateFilter } from '../../date-filter';
import { CustomSearch } from '../../custom-search';
import { DoneByFilter } from '../../done-by-filter';
import { toNumber } from 'lodash';
import { DEFAULT_ROWS_PER_PAGE, NOT_AVAILABLE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import './audit-notes-v2.scss';
import Button from '@atlaskit/button';
import { FinanceRefundReasons, RefundReasons } from '@atlassian/cssxp-types-billing';
import { CustomPanel } from '../../custom-panel';
import { NoDataContentInTable } from '../../no-data-content-table';

// We should be able to enable Actioned By(s) by changing this value to the correct key from the GraphQL response
// once Hydra gets ready with the response. Also we need to add that key our GraphQL schema & the GraphQL query -> fetchAuditNotesForEntity
const KEY_FOR_ACTIONED_BY = 'contactKey';
export interface AuditNotesV2Props {
  id: string;
  entityName: string;
  label?: string;
  idText?: string;
  showFilters?: boolean;
}

type DateRangeFilterParams = {
  createdIsLte?: string;
  createdIsGte?: string;
};
export interface SearchParamsArg extends AuditNotesV2Props {
  dateRangeFilter?: DateRangeFilterParams | null;
  author?: string;
  isForAuthor?: boolean;
}

type AuditNoteMetaDatatype = 'supportTicketNumber' | 'internalNote' | 'financeRefundReason';
type IdKeyInfo = Omit<generatedTypesNew.CommerceNoteSearchParams, 'createdIsLte | createdIsGte | and | or | created'>;

type DateRangeType = {
  from: string;
  to: string;
};
interface FormData {
  searchTerm: string;
}

const defaultSortOptions = {
  sortFields: ['created', 'id'],
  sortFieldsDirections: [generatedTypesNew.SortDirection.DESC, generatedTypesNew.SortDirection.DESC],
};

const defaultAuditNotesSearchOptions: generatedTypesNew.FetchAuditNotesForEntityQueryVariables['searchOptions'] = {
  first: DEFAULT_ROWS_PER_PAGE,
  sort: defaultSortOptions,
};

const wrapInCell = (node: React.ReactElement | string) => {
  return <div className="audit-note-cell">{node}</div>;
};

const makeHead = (headers: string[]) => {
  const cells = headers?.map((header) => {
    return {
      content: wrapInCell(header),
    };
  });
  return {
    cells,
  };
};

type ProcessedAuditNoteType = {
  created: generatedTypesNew.Maybe<string> | undefined;
  body: generatedTypesNew.Maybe<string> | undefined;
  handNote: string;
  supportTicketNumber: string;
  financeRefundReason: string;
  author: generatedTypesNew.Maybe<string> | undefined;
  [KEY_FOR_ACTIONED_BY]: generatedTypesNew.Maybe<string> | undefined;
  creditNoteStatus: string | undefined | null;
};

const processAuditNote = (auditNote: generatedTypesNew.AuditNote): ProcessedAuditNoteType => {
  const created = auditNote?.created;
  const body = auditNote?.body;
  const author = auditNote?.author;
  const metadata = auditNote?.metadata as Array<{ fieldName: string; fieldValue: string }>;
  const metadataRender = metadata?.reduce((acc: Record<AuditNoteMetaDatatype, string>, dataObject) => {
    if (dataObject && dataObject.fieldName) {
      const key = dataObject.fieldName as AuditNoteMetaDatatype;
      acc[key] = dataObject?.fieldValue;
    }
    return acc;
  }, {} as Record<AuditNoteMetaDatatype, string>);
  const handNote = metadataRender?.internalNote;
  const supportTicketNumber = metadataRender?.supportTicketNumber;
  const financeRefundReason = metadataRender?.financeRefundReason
    ? FinanceRefundReasons[metadataRender.financeRefundReason as keyof typeof FinanceRefundReasons]
    : '';
  const contactKey = auditNote?.contactKey;
  const creditNoteStatus = auditNote?.creditNoteStatus;

  return {
    created,
    body,
    financeRefundReason,
    handNote,
    supportTicketNumber,
    author,
    contactKey,
    creditNoteStatus,
  };
};

const makeColumns = (
  auditNotes: ProcessedAuditNoteType[] | undefined,
  isFeatureEnabled: boolean,
  entityName: string
) => {
  return auditNotes?.map((auditNote, index) => {
    const {
      created,
      handNote,
      body,
      supportTicketNumber,
      financeRefundReason,
      contactKey,
      author,
      creditNoteStatus,
    } = auditNote;
    const action = body?.split('-');
    const update = action?.[0];
    const refundReasonBody = RefundReasons[(body || '').toUpperCase() as keyof typeof RefundReasons];
    let updatedBodyValue = body;
    if (body?.includes('Billing_Admin_Updated')) {
      updatedBodyValue = body.replace('Updated', 'Added');
    }
    if (body?.includes('BILLING_ADMIN_UPDATED')) {
      updatedBodyValue = body.replace('UPDATED', 'ADDED');
    }
    return {
      key: `audit-note-v2-${index}`,
      cells: [
        {
          content: wrapInCell(<FormattedDate date={created} showTime />),
        },
        {
          content: wrapInCell(
            <>
              <span>{contactKey}</span>
              {body && (
                <div>
                  <>{refundReasonBody ? 'Refund reason:' : ''}</> {refundReasonBody ?? updatedBodyValue}
                </div>
              )}
              {financeRefundReason && (
                <div>
                  Finance refund reason: <>{financeRefundReason}</>
                </div>
              )}
              {isFeatureEnabled && handNote ? (
                <div className="extra-details" style={{ whiteSpace: 'normal' }}>
                  Note: {handNote}
                </div>
              ) : null}{' '}
              {isFeatureEnabled && supportTicketNumber ? (
                <div className="extra-details">Support Ticket Number: {supportTicketNumber}</div>
              ) : null}
            </>
          ),
        },
        // {
        //   content: wrapInCell(<span>{financeRefundReason ? financeRefundReason : NOT_AVAILABLE}</span>),
        // },
        ...(entityName === EntityWithNotes.INVOICE || entityName === EntityWithNotes.REFUND
          ? [
              {
                content: wrapInCell(creditNoteStatus?.toUpperCase() ?? <NoDataContentInTable />),
              },
            ]
          : []),
        {
          content: wrapInCell(
            <div
              className={classNames({
                email: author && validator.isEmail(author ?? ''),
              })}
            >
              {/* {auditNote[KEY_FOR_ACTIONED_BY] && IS_ACTIONED_BY_DATA_AVAILABLE_IN_CCP
                ? auditNote[KEY_FOR_ACTIONED_BY]
                : NOT_AVAILABLE} */}
              {author ? author : created && update ? 'System' : NOT_AVAILABLE}
            </div>
          ),
        },
      ],
    };
  });
};

const getEmptyStateProps = (error = '') => {
  const emptyStateProps = {
    header: error ? 'Oh no! Something went wrong, please try again ' : NO_DETAILS_TO_SHOW,
    description: error,
  };
  return emptyStateProps;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const prepareEntityParams = (id: string, entityName: string, fn: any) => {
  switch (entityName) {
    case EntityWithNotes.INVOICE:
      return fn({ invoiceKey: id });
    case EntityWithNotes.REFUND:
      return fn({ refundKey: id });
    case EntityWithNotes.ENTITLEMENT:
      return fn({ entitlementKey: id });
    case EntityWithNotes.QUOTE:
      return fn({ quoteKey: id });
    case EntityWithNotes.CONTACT:
      return fn({ contactKey: id });
    case EntityWithNotes.TransactionAccount:
      return fn({ transactionAccountKey: id });
    case EntityWithNotes.CloudSite:
      return fn({ cloudSiteId: id });
    default:
      return undefined;
  }
};

const buildSearchParams = ({
  id,
  idText,
  entityName,
  dateRangeFilter,
  author,
}: SearchParamsArg): generatedTypesNew.CommerceNoteSearchParams | undefined => {
  const dateRange = dateRangeFilter
    ? [
        {
          createdIsLte: dateRangeFilter.createdIsLte,
        },
        {
          createdIsGte: dateRangeFilter.createdIsGte,
        },
      ]
    : [];
  const searchAndParams = [...dateRange, ...(author && author !== '' ? [{ [KEY_FOR_ACTIONED_BY]: author }] : [])];

  const makeSearchParams = (keyInfo: IdKeyInfo): generatedTypesNew.CommerceNoteSearchParams => {
    if (idText) {
      return {
        and: [...searchAndParams, keyInfo, { idText }],
      };
    }
    return {
      and: [...searchAndParams, keyInfo],
    };
  };

  return prepareEntityParams(id, entityName, makeSearchParams);
};

const buildSearchParamsForAuthors = ({ id, entityName }: SearchParamsArg) => {
  const makeSearchParamsForAuthors = (keyInfo: IdKeyInfo): generatedTypesNew.CommerceNoteSearchParams => keyInfo;
  return prepareEntityParams(id, entityName, makeSearchParamsForAuthors);
};

const search = (auditNotes: ProcessedAuditNoteType[], term: string): ProcessedAuditNoteType[] => {
  const filteredAuditNotes = auditNotes.filter((auditNote) => {
    return (
      auditNote?.body?.includes(term) ||
      auditNote?.handNote?.includes(term) ||
      auditNote?.supportTicketNumber?.includes(term) ||
      auditNote?.financeRefundReason?.includes(term)
    );
  });
  return filteredAuditNotes;
};

export const AuditNotesV2 = ({
  id,
  entityName,
  label = 'Audit trail',
  idText,
  showFilters = false,
}: AuditNotesV2Props) => {
  // Authors and Actioned By(s) are synonymous.
  const [dateRangeForAuditoNotes, setDateRange] = React.useState<DateRangeType>({ from: '', to: '' });
  const [selectedAuthor, setSelectedAuthor] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [searchTerm, setSearchTerm] = React.useState<string | null>();
  const [isFilterApplied, setFilterApplied] = React.useState<boolean>(false);
  const [selectedDateRangeValue, setSelectedDateRangeValue] = React.useState('');
  const [hasAuthorsBeenFetched, setHasAuthorsBeenFetched] = React.useState(false);

  const [isEnabled] = useFeatureToggle();
  let panelPageName = '';

  if (entityName === 'transactionAccount') {
    panelPageName = 'for this billing account';
  } else if (entityName === 'refund') {
    panelPageName = 'for this credit note';
  } else if (entityName === 'invoice') {
    panelPageName = 'for this invoice';
  } else if (entityName === 'cloudsite') {
    panelPageName = 'for this cloud site';
  }

  const panelHeading = 'audit trail' + ' ' + panelPageName;

  const { from, to } = dateRangeForAuditoNotes;
  const dateRangeFilter =
    from && from !== '' && to && to !== ''
      ? {
          createdIsLte: (toNumber(to) / 1000).toString(),
          createdIsGte: (toNumber(from) / 1000).toString(),
        }
      : null;
  const searchParams = buildSearchParams({
    id,
    idText,
    entityName,
    dateRangeFilter,
    author: selectedAuthor,
  });

  const [fetchAuthors, { loading: areAuthorsLoading, data: authorsFromHydra }] = useLazyQuery<
    generatedTypesNew.FetchAuditNoteAuthorsQuery,
    generatedTypesNew.FetchAuditNoteAuthorsQueryVariables
  >(fetchAuditNoteAuthors, {
    onCompleted: () => {
      setHasAuthorsBeenFetched(true);
    },
  });

  const { data, loading, error, refetch } = useQuery<
    generatedTypesNew.FetchAuditNotesForEntityQuery,
    generatedTypesNew.FetchAuditNotesForEntityQueryVariables
  >(fetchAuditNotesForEntity, {
    variables: {
      searchParams,
      searchOptions: defaultAuditNotesSearchOptions,
    },
    displayName: 'fetchAuditNotesByEntityId',
    notifyOnNetworkStatusChange: true,
    ...(!hasAuthorsBeenFetched && {
      // To fetch authors only once.
      onCompleted: (data) => {
        // This basically fetches all the authors available to populate the Actioned By filter.
        const total = data?.auditNotes?.totalCount ?? 0;
        if (total > 0) {
          fetchAuthors({
            variables: {
              searchParams: buildSearchParamsForAuthors({ id, entityName }),
              searchOptions: {
                first: total,
              },
            },
          });
        }
      },
    }),
  });

  const queryResults = data?.auditNotes;
  const auditTrailNodes = queryResults?.nodes as generatedTypesNew.AuditNote[];

  const totalCount = queryResults?.totalCount ?? 0;

  const actionedBysFromHydra = (authorsFromHydra?.auditNotes.nodes ?? []) as generatedTypesNew.AuditNote[];
  // eslint-disable-next-line no-unused-vars
  const authors = [
    ...new Set((actionedBysFromHydra?.map(({ contactKey }) => contactKey) ?? []) as string[]).values(),
  ].filter(Boolean);

  const getDateRange = (value: DateRangeType) => {
    setDateRange(value);
    setFilterApplied(true);
  };

  const onRowsPerPageChange = (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    refetch({
      searchOptions: {
        first: rowsCount,
        sort: defaultSortOptions,
      },
    });
    setCurrentPage(1);
  };

  const onActionedBySelected = (selectedAuthor: string) => {
    setSelectedAuthor(selectedAuthor);
    setFilterApplied(true);
  };

  const onPageChange = React.useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: (newPage - 1) * rowsPerPage,
          sort: defaultSortOptions,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  let processedAuditNotes = auditTrailNodes?.map((auditNote) => processAuditNote(auditNote)) ?? [];
  if (searchTerm && searchTerm !== '') {
    processedAuditNotes = search(processedAuditNotes, searchTerm);
  }

  const columns = error ? [] : makeColumns(processedAuditNotes, isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE), entityName);

  const onSearch = async (formData: FormData) => {
    const term: string = formData && formData.searchTerm;
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const headers = useMemo(() => {
    return entityName === EntityWithNotes.INVOICE || entityName === EntityWithNotes.REFUND
      ? ['Date', 'Details', 'Status', 'Actioned by']
      : ['Date', 'Details', 'Actioned by'];
  }, [entityName]);

  return (
    <div className="audit-notes-v2-container" data-testid="audit-notes-log">
      <CustomPanel
        testId="audit-trail-txa"
        heading={label}
        expandedHeader={`Collapse to hide ${panelHeading}`}
        collapsedHeader={`Expand to view ${panelHeading}`}
      >
        <DynamicResultTable
          head={makeHead(headers)}
          rows={columns}
          loadingSpinnerSize="small"
          totalCount={totalCount}
          emptyStateProps={getEmptyStateProps(error && error.toString())}
          isLoading={loading}
          isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onRowChange={onRowsPerPageChange}
          showFilters={showFilters}
        >
          <div className="filters">
            <CustomSearch placeholder={'Search all audit trail'} onSearch={onSearch} isLoading={loading} />
            <DateFilter
              dateValue={getDateRange}
              selected={selectedDateRangeValue}
              onSelected={(selected: string) => {
                setSelectedDateRangeValue(selected);
              }}
            />
            <DoneByFilter
              label="Actioned By"
              values={[] /* authors - Once Actioned By(s) are enabled we can just use authors to populate the filter*/}
              onSelected={onActionedBySelected}
              selected={selectedAuthor}
              isLoading={areAuthorsLoading}
            />
            {isFilterApplied && (
              <Button
                appearance="link"
                testId="filter-reset-link"
                onClick={() => {
                  setFilterApplied(false);
                  setSelectedAuthor('');
                  setDateRange({ from: '', to: '' });
                  setSelectedDateRangeValue('');
                }}
              >
                Clear all filters
              </Button>
            )}
          </div>
        </DynamicResultTable>
      </CustomPanel>
    </div>
  );
};
